import type { ReactNode } from "react";
import { CraigFace } from "./CraigFace";
import { Wordmark } from "./Brand";

/* Shared furniture. Quiet by design — see styles.css for the reasoning. */

export function Card({
  children,
  className = "",
  style,
}: {
  children: ReactNode;
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <div className={`card ${className}`} style={style}>
      {children}
    </div>
  );
}

export function Button({
  children,
  onClick,
  variant = "primary",
  disabled,
  title,
  className = "",
  type = "button",
}: {
  children: ReactNode;
  onClick?: () => void;
  variant?: "primary" | "quiet";
  disabled?: boolean;
  title?: string;
  className?: string;
  type?: "button" | "submit";
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`btn ${variant === "primary" ? "btn-primary" : "btn-quiet"} ${className}`}
    >
      {children}
    </button>
  );
}

/**
 * The way back to the dashboard, for the top bar of crAIg's own screens.
 *
 * One component rather than five copies, because it has to look and sit
 * the same everywhere — a button that moves between screens is one people
 * stop reaching for. It lives at the LEFT end of every top bar, and where
 * a screen also has a "← Back" one level up, this sits beside it: back is
 * one step, this is all the way home.
 *
 * The house is drawn rather than typed. `⌂` is missing from several of the
 * mono faces this product runs in, and a top bar with a hollow box in it
 * is not a top bar anybody trusts.
 *
 * Do NOT put this on a business's customer intake. That page belongs to
 * the practice — see Header below.
 */
export function DashboardLink() {
  return (
    <a
      href="/dashboard"
      className="btn btn-quiet btn-term shrink-0"
      style={{ padding: "0.3rem 0.6rem", display: "inline-flex", alignItems: "center", gap: 6 }}
      title="Everything in your account"
      aria-label="Go to your dashboard"
    >
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M3 10.5 12 3l9 7.5M5.5 9v11h13V9"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <span className="hidden sm:inline">Dashboard</span>
    </a>
  );
}

/**
 * The practice's header.
 *
 * Their name is the largest thing on the page and crAIg is not mentioned
 * here at all — the customer came to deal with them. crAIg introduces
 * himself in the conversation, where it's relevant, and signs off at the
 * bottom of the file panel.
 *
 * `homeHref` is the one exception, and it is off by default for a reason:
 * on a business's intake there is nowhere for it to point. letcraig.ai
 * sells crAIg to businesses, so a plumber's customer clicking "home" would
 * land on a pitch for the software their plumber bought — and the practice
 * whose page it is would be entitled to be annoyed about it.
 *
 * It is set only on crAIg's own setup conversation, where home really is
 * home and someone who wanted to read a bit more first is otherwise stuck
 * with the browser's back button. Where it goes depends on who's reading —
 * see `useHomeHref` in session.ts — so the caller names the destination
 * too, rather than this button claiming "Home" and delivering a dashboard.
 */
export function Header({
  practiceName,
  tagline,
  logoUrl,
  progress,
  theme,
  homeHref,
  homeLabel = "Home",
  onToggleTheme,
  onRestart,
}: {
  practiceName: string;
  tagline?: string;
  logoUrl?: string;
  progress?: number;
  theme: "light" | "dark";
  homeHref?: string;
  homeLabel?: string;
  onToggleTheme(): void;
  onRestart(): void;
}) {
  return (
    <header
      style={{
        background: "var(--surface)",
        borderBottom: "1px solid var(--line)",
        position: "relative",
        zIndex: 20,
      }}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          {homeHref && (
            <a
              href={homeHref}
              className="btn btn-quiet btn-term shrink-0"
              style={{ padding: "0.4rem 0.6rem", fontSize: 13 }}
              aria-label={`Back to ${homeLabel.toLowerCase() === "home" ? "the home page" : homeLabel.toLowerCase()}`}
            >
              <span aria-hidden="true">←</span>
              <span className="hidden sm:inline" style={{ marginLeft: 6 }}>
                {homeLabel}
              </span>
            </a>
          )}
          {logoUrl && (
            <img
              src={logoUrl}
              alt=""
              style={{ height: 28, width: "auto", borderRadius: 6 }}
            />
          )}
          <div className="min-w-0">
            <div
              className="truncate"
              style={{ fontWeight: 650, fontSize: 15.5, letterSpacing: "-0.01em" }}
            >
              {practiceName}
            </div>
            {tagline && (
              <div className="truncate hidden sm:block" style={{ fontSize: 12.5, color: "var(--ink-3)" }}>
                {tagline}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={onToggleTheme}
            className="btn btn-quiet"
            style={{ padding: "0.4rem 0.6rem" }}
            aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            title={theme === "dark" ? "Light mode" : "Dark mode"}
          >
            {theme === "dark" ? "☀" : "☾"}
          </button>
          <button
            type="button"
            onClick={onRestart}
            className="btn btn-quiet"
            style={{ padding: "0.4rem 0.7rem", fontSize: 13 }}
          >
            <span className="hidden sm:inline">Start over</span>
            <span className="sm:hidden" aria-label="Start over">↻</span>
          </button>
        </div>
      </div>

      {progress !== undefined && (
        <div style={{ height: 3, background: "var(--line)" }}>
          <div
            style={{
              height: "100%",
              width: `${progress}%`,
              background: "var(--accent)",
              transition: "width .5s cubic-bezier(.22,1,.36,1)",
            }}
          />
        </div>
      )}
    </header>
  );
}

export function Bubble({
  from,
  children,
  speaking,
}: {
  from: "you" | "craig";
  children: ReactNode;
  speaking?: boolean;
}) {
  if (from === "you") {
    return (
      <div className="flex justify-end fade-up">
        <div
          className="bubble-you px-3.5 py-2.5 max-w-[85%]"
          style={{
            background: "var(--accent)",
            color: "var(--accent-ink)",
            borderRadius: "14px 14px 4px 14px",
            fontSize: 14.5,
          }}
        >
          {children}
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-2.5 items-start fade-up">
      <CraigFace size={30} speaking={speaking} />
      <div
        className="bubble-craig px-3.5 py-2.5 max-w-[88%]"
        style={{
          background: "var(--surface)",
          border: "1px solid var(--line)",
          borderRadius: "4px 14px 14px 14px",
          fontSize: 14.5,
        }}
      >
        {children}
      </div>
    </div>
  );
}

/** A tick-list option, used when someone asks to see the whole list. */
export function SelectRow({
  active,
  onClick,
  children,
  hint,
}: {
  active?: boolean;
  onClick(): void;
  children: ReactNode;
  hint?: string | null;
}) {
  return (
    <button type="button" onClick={onClick} aria-pressed={active} className="chip-select">
      <span style={{ fontWeight: 550, fontSize: 14 }}>{children}</span>
      {hint && (
        <span className="block mt-0.5" style={{ fontSize: 12.5, color: "var(--ink-3)", lineHeight: 1.45 }}>
          {hint}
        </span>
      )}
    </button>
  );
}

/** Panel heading — small, uppercase, unobtrusive. */
export function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        fontSize: 11,
        fontWeight: 650,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        color: "var(--ink-3)",
      }}
    >
      {children}
    </div>
  );
}

export { Wordmark };
